// Mobile-optimized survey list
// Large touch-friendly cards with easy navigation

import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight, Building2, Plus } from 'lucide-react'
import useSurveyStore from '../../stores/surveyStore'
import { formatDate } from '../../utils/helpers'
import { SURVEY_STATUS_LABELS, SURVEY_STATUS_COLORS } from '../../utils/constants'

export default function MobileSurveyList() {
  const navigate = useNavigate()
  const surveys = useSurveyStore(state => state.surveys)
  const loadSurveys = useSurveyStore(state => state.loadSurveys)
  const loading = useSurveyStore(state => state.loading)

  useEffect(() => {
    loadSurveys()
  }, [loadSurveys])

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Header */}
      <div className="bg-primary-700 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 hover:bg-primary-600 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">All Surveys</h1>
        </div>
        <button
          onClick={() => navigate('/survey/new')}
          className="p-2 hover:bg-primary-600 rounded-lg"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Survey list - scrollable */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          </div>
        ) : surveys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Building2 className="w-16 h-16 text-slate-300 mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">No surveys yet</h2>
            <p className="text-slate-500 text-center mb-6">Create your first survey to get started</p>
            <button
              onClick={() => navigate('/survey/new')}
              className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Survey
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {surveys.map((survey) => (
              <button
                key={survey.id}
                onClick={() => navigate(`/survey/${survey.id}`)}
                className="w-full bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex items-center gap-3 active:bg-slate-50 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {survey.propertyName}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">
                    {survey.address}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SURVEY_STATUS_COLORS[survey.status]}`}>
                      {SURVEY_STATUS_LABELS[survey.status]}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatDate(survey.createdAt)}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
